import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { getCountryCallingCode } from 'libphonenumber-js';

import { PROJECT, PRODUCTS_KEY } from '../constants/api';

import * as api from './apiClient';

// Initialize country data with English locale
try {
    countries.registerLocale(enLocale);
} catch (error) {
    console.error('Failed to register en-US locale for i18n-iso-countries:', error);
}

interface CountryObject {
    code: string;
    name: string;
    dialCode: string;
}

interface ProjectSettings {
    countries: string[];
}

interface CTAttribute {
    name: string;
    value: string[];
}

interface CTProduct {
    masterData: {
        current: {
            masterVariant: {
                attributes: CTAttribute[];
            };
        };
        staged: {
            masterVariant: {
                attributes: CTAttribute[];
            };
        };
    };
}

const AMBER_COUNTRY_PRODUCT_KEY = import.meta.env.VITE_AMBER_COUNTRY_PRODUCT_KEY;


export const countryService = {
    /**
     * Fetch project settings including supported countries from backend API
     * @returns {Promise<ProjectSettings | null>} Project settings with countries array
     */
    async fetchProjectSettings(): Promise<ProjectSettings | null> {
        try {
            const res = await api.get(PROJECT);

            if (!res.ok) throw new Error(`Failed to fetch project settings: ${res.status}`);
            return (await res.json()) as ProjectSettings;
        } catch (error) {
            console.error('Failed to fetch project settings from CommerceTools:', error);
            // Return null to indicate failure - let calling code handle fallback
            return null;
        }
    },

    /**
     * Fetch amber country ISO codes from backend API
     * @returns {Promise<string[]>} Array of amber country ISO codes
     */
    async fetchAmberCountries(): Promise<string[]> {
        try {
            const res = await api.get(PRODUCTS_KEY(AMBER_COUNTRY_PRODUCT_KEY));

            if (!res.ok) throw new Error(`Failed to fetch amber countries product: ${res.status}`);
            const product = (await res.json()) as CTProduct;

            // Try current (published) data first
            let attributes = product?.masterData?.current?.masterVariant?.attributes;

            // If current attributes are empty, try staged data
            if (!attributes || attributes.length === 0) {
                attributes = product?.masterData?.staged?.masterVariant?.attributes;
            }

            const amberAttribute = attributes?.find((attr) => attr.name === 'amber-country-iso-codes');

            const amberCountryCodes = amberAttribute?.value || [];

            return amberCountryCodes;
        } catch (error) {
            console.error('Failed to fetch amber countries product:', error);
            return [];
        }
    },

    /**
     * Convert ISO country codes to full country objects with names and dial codes
     * @param {string[]} countryCodes - Array of ISO country codes
     * @returns {CountryObject[]} Array of country objects with code, name, and dialCode
     */
    convertCountriesToObjects(countryCodes: string[]): CountryObject[] {
        return countryCodes.map(code => {
            const countryName = countries.getName(code, 'en');
            let dialCode = '';

            try {
                dialCode = `+${getCountryCallingCode(code as never)}`;
            } catch (error) {
                console.error(`Failed to get country calling code for ${code}:`, error);
                dialCode = '';
            }

            return {
                code,
                name: countryName || code, // Fallback to code if name not found
                dialCode
            };
        }).filter(country => country.name !== country.code); // Filter out countries where name conversion failed
    },

    /**
     * Get all available countries with names and dial codes
     * @returns {Promise<CountryObject[]>} Array of country objects
     */
    async getAllCountries(): Promise<CountryObject[]> {
        const projectSettings = await this.fetchProjectSettings();
        if (!projectSettings || !projectSettings.countries) {
            throw new Error('Failed to fetch countries from CommerceTools API');
        }

        const countryObjects = this.convertCountriesToObjects(projectSettings.countries);
        if (countryObjects.length === 0) {
            throw new Error('No valid countries found after conversion');
        }

        return countryObjects;
    },

    /**
     * Check if a country code is in the amber list
     * @param {string} countryCode - ISO country code to check
     * @returns {Promise<boolean>} True if country is amber, false otherwise
     */
    async isAmberCountry(countryCode: string): Promise<boolean> {
        try {
            const amberCountries = await this.fetchAmberCountries();
            return amberCountries.includes(countryCode);
        } catch (error) {
            console.error(`Failed to check if ${countryCode} is amber:`, error);
            return false;
        }
    },

    /**
     * Check if a country is amber (always fetches fresh data)
     * @param {string} countryCode - ISO country code to check
     * @returns {Promise<boolean>} True if country is amber, false otherwise
     */
    async isAmberCountryFromCache(countryCode: string): Promise<boolean> {
        try {
            // Always fetch fresh data - no caching
            const amberCountries = await this.fetchAmberCountries();
            return amberCountries.includes(countryCode);
        } catch (error) {
            console.error(`Failed to check if ${countryCode} is amber (from cache logic):`, error);
            return false;
        }
    },

    /**
     * Get country by code from the full countries list
     * @param {string} countryCode - ISO country code
     * @returns {Promise<CountryObject|null>} Country object or null
     */
    async getCountryByCode(countryCode: string): Promise<CountryObject | null> {
        const countriesList = await this.getAllCountries();
        return countriesList.find(country => country.code === countryCode) || null;
    }
};

/* ── Convenience standalone exports (previously in constants/countries.js) ── */

/**
 * Get all available countries from CommerceTools API.
 * @returns {Promise<CountryObject[]>} Array of country objects
 * @throws {Error} If API fails to fetch countries
 */
export const getCountries = async (): Promise<CountryObject[]> => {
    const countriesList = await countryService.getAllCountries();
    if (!countriesList || countriesList.length === 0) {
        throw new Error('No countries available from CommerceTools API');
    }
    return countriesList;
};

/**
 * Check if a country is in the amber list.
 * @param {string} countryCode - ISO country code
 * @returns {Promise<boolean>}
 */
export const isAmberCountry = async (countryCode: string): Promise<boolean> => {
    return countryService.isAmberCountryFromCache(countryCode);
};
